import React, {useEffect, useRef, useState} from "react";
import Error from "../../../../Alerts/Error";
import {fileTypeFromBlob} from 'file-type';
import Spinner from "../../../../Spinner";
import { parseBlob } from 'music-metadata';
import { queuePositionLabel } from "../../../../../util/uploadQueue";
import CancelUpload, {useUploadCancel} from "./CancelUpload";
import { claimSelection, releaseClaim, uploadOwner, DUPLICATE_IN_ENTRY_MESSAGE } from "../../../../../util/duplicateFiles";
import useMediaRetry from "../../../../../util/mediaRetry";


// Containers in the MP4/QuickTime family. The same phone produces a file detected as any of
// these depending on how it left the device (direct upload, computer transfer, shared through
// another app), so they are accepted interchangeably: a job that allows .mov must not reject
// the .mp4 the export path happens to write, or the other way round.
const MP4_FAMILY = ['.mp4', '.mov'];

// Keeps the player the size it is while uploading instead of growing to the video's own
// dimensions once a source is set, matching the previews on the Review page.
const PREVIEW_STYLE = {maxHeight: "150px", maxWidth: "100%", width: "auto", height: "auto"};

// How long the checks below may spend reading the file before giving up on it.
//
// Neither of them is guaranteed to finish. A media element handed a .mov whose metadata sits at
// the end of the file -- which is how a phone or a camera writes a recording -- reads forward
// looking for it, and on a file the operating system cannot hand over quickly (a cloud
// placeholder, an SD card, a phone mounted over USB) it can read for hours without ever firing
// either loadedmetadata or error. The contributor sees "Preparing upload..." for that whole time,
// because nothing has reached the upload yet and there is no progress to report.
//
// Every later stage of the upload has a stall timeout; these had none. Giving up here costs only
// the optional length and sample rate checks, which are already skipped whenever the browser
// cannot read the file, so the upload goes ahead either way -- which is much the better outcome.
const METADATA_TIMEOUT_MS = 30 * 1000;

// An upload that dies on its way to Spaces is hardly ever the file's fault, and it is never
// something the contributor can act on. The two we have traced are a connection that goes away
// mid-part (ERR_NETWORK_CHANGED, ERR_NAME_NOT_RESOLVED) and a file that stops being readable
// because its folder moved or its drive slept (ERR_FILE_NOT_FOUND, NotFoundError). Both come
// back on their own, and the upload resumes from the parts already in the bucket, so all the old
// behaviour really asked for was that the same file be found and picked again -- on an entry
// holding seventy numbered videos, that hunt is where the wrong file gets attached.
//
// So wait and go again instead, keeping the file this input already holds. Delays climb to two
// minutes so a long outage is not spent hammering a dead network, and a returning connection
// cuts the wait short.
const RETRY_DELAYS_MS = [5000, 15000, 30000, 60000, 120000];
const MAX_UPLOAD_ATTEMPTS = 20;

function retryDelay(attempt) {
    return RETRY_DELAYS_MS[Math.min(attempt - 1, RETRY_DELAYS_MS.length - 1)];
}

function extensionAllowed(detectedExtension, allowedExtensions) {
    const detected = '.' + detectedExtension;

    if (allowedExtensions.includes(detected)) {
        return true;
    }
    return MP4_FAMILY.includes(detected) && allowedExtensions.some(allowed => MP4_FAMILY.includes(allowed));
}

export default function Video({formId, uploadFile, setFile, fileKey, file}) {
    const fileRef = useRef();
    const videoRef = useRef();
    const [errors, setErrors] = useState([]);
    const [loading, setLoading] = useState(false);
    const upload = useUploadCancel();
    const [progress, setProgress] = useState(null); // {loaded, total, bps, phase} while working, null otherwise
    const [playbackUnsupported, setPlaybackUnsupported] = useState(false);
    const speedRef = useRef(null); // {loaded, time, bps} sampler for upload speed
    const [retrying, setRetrying] = useState(null); // {attempt, remaining} while waiting to go again
    // The file a failed upload was holding, so a retry does not need it picked again. The input
    // element keeps its own selection too, but only this survives the input being cleared.
    const [pending, setPending] = useState(null);
    const retryNowRef = useRef(null); // resolves the current wait early

    const previewSrc = fileKey ? process.env.REACT_APP_ENDPOINT + '/files/' + fileKey + '?redirect=1' : null;
    // Only once the retries are spent: a bucket hiccup on the first range must not tell the
    // contributor their recording format is the problem.
    const playback = useMediaRetry(videoRef, previewSrc, {onGiveUp: () => setPlaybackUnsupported(true)});

    // Resolves to null rather than rejecting when the browser cannot read the file. That is the
    // normal case for the format we now accept: an iPhone on its default setting records HEVC,
    // which Chrome and Firefox cannot decode, so reading the metadata fails on a file that is
    // perfectly good. Treating it as "duration unknown" lets the upload through; rejecting it
    // would turn away most iPhone footage.
    function getDuration(file) {
        return new Promise((resolve) => {
            const url = URL.createObjectURL(file);
            const video = document.createElement('video');
            let timer;
            let settled = false;

            // Every exit goes through here so the element is told to stop reading the file. Left
            // alone it keeps the read going after we have stopped waiting for it, competing for
            // the same disk the hash is about to stream through.
            function settle(duration) {
                if (settled) {
                    return;
                }
                settled = true;
                clearTimeout(timer);
                video.onloadedmetadata = null;
                video.onerror = null;
                video.removeAttribute("src");
                video.load();
                URL.revokeObjectURL(url);
                resolve(duration);
            }

            timer = setTimeout(() => {
                console.warn(`Reading the video duration took longer than ${METADATA_TIMEOUT_MS / 1000}s; skipping the length check for`, file.name);
                settle(null);
            }, METADATA_TIMEOUT_MS);

            video.preload = "metadata";

            video.onloadedmetadata = () => settle(video.duration);

            video.onerror = () => {
                console.warn("Could not read the video duration in this browser; skipping the length check for", file.name);
                settle(null);
            };

            video.src = url;
        });
    }

    // Also null on failure: not being able to demux the audio of a container this browser does
    // not support is not a reason to reject the file.
    async function getSampleRate(file) {
        try {
            // Same unbounded read as the duration above, through a different library: parseBlob
            // walks the container looking for the audio track and has no timeout of its own.
            const metadata = await Promise.race([
                parseBlob(file),
                new Promise((_, fail) => setTimeout(
                    () => fail(new Error(`Reading the audio sample rate took longer than ${METADATA_TIMEOUT_MS / 1000}s`)),
                    METADATA_TIMEOUT_MS,
                )),
            ]);
            return metadata.format.sampleRate || null;
        }
        catch (e) {
            console.warn("Could not read the audio sample rate; skipping the sample rate check for", file.name, e);
            return null;
        }
    }

    // Clears the chosen file and tells the contributor why. Every rejection goes through here so
    // no path can empty the input while leaving nothing on screen.
    function reject(message) {
        fileRef.current.value = null;
        if (!fileKey) { // if there is already a file, don't hide the video as it will be removed if upload fails
            videoRef.current.classList.add("d-none");
            videoRef.current.controls = false;
        }
        setErrors([message]);
    }

    // The upload failed and cannot be worth retrying on its own, or has run out of attempts. The
    // file stays with the input either way: it is the one thing the contributor would otherwise
    // have to go and find again.
    function failUpload(chosen, message) {
        setPending(chosen);
        setErrors([message]);
    }

    // Waits out the backoff, or stops as soon as the browser says the connection is back.
    function waitToRetry(attempt) {
        return new Promise((resolve) => {
            const delay = retryDelay(attempt);
            let remaining = Math.round(delay / 1000);
            setRetrying({attempt, remaining});

            const tick = setInterval(() => {
                remaining -= 1;
                setRetrying({attempt, remaining: Math.max(0, remaining)});
            }, 1000);

            const finish = () => {
                clearInterval(tick);
                clearTimeout(timer);
                window.removeEventListener("online", finish);
                retryNowRef.current = null;
                setRetrying(null);
                resolve();
            };

            const timer = setTimeout(finish, delay);
            window.addEventListener("online", finish);
            retryNowRef.current = finish; // so "Try now" doesn't have to sit through the countdown
        });
    }

    function onUploadProgress(loaded, total, phase, position) {
        const now = Date.now();
        // Reading the file to hash it moves at disk speed, which is nothing like the
        // wire speed that follows. Restart the sampler when the phase changes so the
        // first upload estimate isn't anchored to it.
        if (!speedRef.current || speedRef.current.phase !== phase) {
            speedRef.current = {loaded, time: now, bps: 0, phase};
        }
        const s = speedRef.current;
        const dt = (now - s.time) / 1000;
        if (dt >= 0.5) { // sample twice a second, smooth with an exponential moving average
            const inst = Math.max(0, loaded - s.loaded) / dt;
            s.bps = s.bps ? s.bps * 0.6 + inst * 0.4 : inst;
            s.loaded = loaded;
            s.time = now;
        }
        setProgress({loaded, total, bps: s.bps, phase, position});
    }

    // A refusal — a duplicate, an oversized file, a closed job — is answered by uploadFile itself
    // with its own message and an empty key, so only a thrown error reaches here, and every one
    // of those is worth another go: the parts already in the bucket are still there, so a retry
    // picks up where this attempt stopped rather than starting the gigabytes again.
    //
    // A cancel answers with no key, like a refusal, and is never retried — including one pressed
    // while waiting to retry.
    async function uploadWithRetries(chosen, signal) {
        for (let attempt = 1; ; attempt++) {
            try {
                speedRef.current = null;
                return await uploadFile(chosen, undefined, onUploadProgress, signal);
            }
            catch (e) {
                console.error(`Upload attempt ${attempt} failed for ${chosen.name}.`, e);
                if (signal.aborted) {
                    return undefined;
                }
                if (attempt >= MAX_UPLOAD_ATTEMPTS) {
                    throw e;
                }
                setProgress(null);
                await waitToRetry(attempt);
                if (signal.aborted) {
                    return undefined;
                }
            }
        }
    }

    function cancelUpload() {
        upload.cancel();
        retryNowRef.current && retryNowRef.current(); // ends a wait to retry, which then sees the cancel
    }

    // Shared by picking a file and by retrying one that is already held, so a retry runs exactly
    // what the original attempt ran. A pick hands over the signal its checks already ran under.
    async function startUpload(chosen, signal = upload.begin()) {
        try {
            setLoading(true);
            setErrors([]);
            setPending(null);

            const key = await uploadWithRetries(chosen, signal);

            if (key) {
                setFile(key, chosen.name);
                fileRef.current.value = null;
            } else if (fileKey) {
                // if there is already a file key, and new file is couldn't be uploaded, keep the old file key
                fileRef.current.value = null;
            } else {
                fileRef.current.value = null;
                videoRef.current.classList.add("d-none");
                videoRef.current.controls = false;
                setFile('', '');
            }
        }
        catch (e) {
            // Out of attempts. Say what actually went wrong rather than "something went wrong":
            // a contributor whose drive went to sleep can act on that, and support can read it
            // off a screenshot instead of asking for a console.
            console.error(e);
            failUpload(chosen, `Your video could not be uploaded after several attempts (${e.message}). `
                + "Check that the file is still in the same folder and that its drive is connected, "
                + "then press Try again — the upload carries on from where it stopped.");
        }
        finally {
            upload.end();
            setRetrying(null);
            setLoading(false);
            setProgress(null);
        }
    }

    async function onChange() {
        // Held here rather than read off the element again after each await: one upload runs at a
        // time and the input is disabled while it does, but the checks below happen before that.
        const chosen = fileRef.current.files[0];
        if (!chosen) {
            return;
        }

        // Turned away the moment it is picked rather than when the queue gets round to hashing it:
        // a file waits minutes for its turn, and the second copy is picked during that wait, while
        // the first is still being checked — which is how this was reported (DEV-3191).
        const owner = uploadOwner(formId, file.id);
        if (!claimSelection(chosen, owner)) {
            return reject(DUPLICATE_IN_ENTRY_MESSAGE);
        }

        // A file the checks below turn away is not this input's after all, so it must stop being
        // held against the others. One that reaches the upload keeps its claim, which the upload
        // itself releases if it fails.
        let reachedUpload = false;

        try {
            setLoading(true);
            setErrors([]);
            const signal = upload.begin();

            let {size} = chosen;
            size /= 1000000;

            // null when this browser cannot decode the file, in which case the length checks
            // below are skipped rather than failed.
            const duration = await getDuration(chosen);

			if (file.properties.minSamplingRate || file.properties.maxSamplingRate) {
				const sampleRate = await getSampleRate(chosen);

				if (sampleRate !== null && file.properties.minSamplingRate && sampleRate < Number(file.properties.minSamplingRate)) {
					return reject(`Please make sure to upload a file with a minimum sample rate of ${file.properties.minSamplingRate} Hz.`);
				}
				if (sampleRate !== null && file.properties.maxSamplingRate && sampleRate > Number(file.properties.maxSamplingRate)) {
					return reject(`Please make sure to upload a file with a maximum sample rate of ${file.properties.maxSamplingRate} Hz.`);
				}
			}
            if (size > Number(file.properties.maxFileSize)) {
                reject(`File exceeded maximum file size of ${file.properties.maxFileSize} MB`);
            }
            else if (size < Number(file.properties.minFileSize)) {
                reject(`File does not have the minimum file size of ${file.properties.minFileSize} MB`);
            }
            else if (duration !== null && duration < Number(file.properties.minVideoLength)) {
                reject(`Video duration does not have the minimum length of ${file.properties.minVideoLength} seconds`);
            }
            else if (duration !== null && duration > Number(file.properties.maxVideoLength)) {
                reject(`Video duration exceeds the maximum length of ${file.properties.maxVideoLength} seconds`);
            }
            else {
                const fileType = await fileTypeFromBlob(chosen);
                if (fileType && !fileType.mime.startsWith('video')) {
                    return reject("This is not a video file.");
                }
                // check for the correct file extension
                if (fileType && !extensionAllowed(fileType.ext, file.properties.extensions)) {
                    return reject("The extension of your file is not allowed, the allowed extensions are: " + file.properties.extensions.toString());
                }
                //probably a text file then
                if (!fileType) {
                    return reject("This is not a video file.");
                }

                // The checks are done; from here on the file is the upload's, and its own
                // retrying and error handling take over.
                reachedUpload = true;
                return await startUpload(chosen, signal);
            }
        }
        catch (e) {
            // Anything unexpected still has to say so. Clearing the input with no message leaves
            // the contributor looking at an empty field, assuming they did something wrong.
            console.error(e);
            reject("Something went wrong while adding your video. Please try again, and let us know if it keeps happening.");
        }
        finally {
            if (!reachedUpload) {
                releaseClaim(owner);
            }
            upload.end();
            setLoading(false);
            setProgress(null);
        }
    }

    useEffect(() => {
        try {
            setPlaybackUnsupported(false);
            videoRef.current.pause();
            if (previewSrc) {
                videoRef.current.src = previewSrc;
                videoRef.current.controls = true;
                videoRef.current.classList.remove("d-none");
            }
            else {
                videoRef.current.classList.add("d-none");
                videoRef.current.controls = false;
            }
        }
        catch (e) {
            console.error(e);
            videoRef.current.classList.add("d-none");
            videoRef.current.controls = false;
        }
    }, [previewSrc]);

    return (
        <div className={"overflow-x-auto"}>
            <label htmlFor={formId + file.id} className={"form-label"}>{file.name}:</label>


            {/* Disabled while one is going: a second pick would start a second upload sharing the
                same connection, and the two would race for this input's single answer. */}
            <input disabled={loading} id={formId + file.id} form={formId} required={!fileKey} ref={fileRef} onChange={onChange} type={"file"} className={"form-control"} name={file.name}
                    accept={file.properties.extensions.toString()}/>

            {/* The wait between attempts. It says the upload is not lost, because the old
                behaviour — an error and an emptied input — taught contributors that it was. */}
            {retrying && (
                <div className="mt-3 alert alert-warning py-2 mb-0">
                    <small>
                        The upload was interrupted — the connection dropped, or the file could not be
                        read. Trying again in {retrying.remaining}s (attempt {retrying.attempt + 1} of {MAX_UPLOAD_ATTEMPTS}).
                        Nothing already uploaded is lost, and you do not need to select the file again.
                    </small>
                    <button type="button" className="btn btn-sm btn-outline-secondary ms-2"
                            onClick={() => retryNowRef.current && retryNowRef.current()}>Try now</button>
                </div>
            )}

            {/* Still holding the file a failed upload was given, so the retry costs a click
                rather than finding video 43 of 70 in a folder again. */}
            {pending && !loading && (
                <div className="mt-2">
                    <button type="button" className="btn btn-sm btn-primary"
                            onClick={() => startUpload(pending)}>Try again — {pending.name}</button>
                </div>
            )}

            {loading && !retrying && (progress && progress.total ?
                (() => {
                    const pct = Math.min(100, Math.round((progress.loaded / progress.total) * 100));
                    const bps = progress.bps || 0;
                    const mbps = (bps / 1048576).toFixed(1);
                    const remaining = bps > 0 ? Math.round((progress.total - progress.loaded) / bps) : null;
                    const eta = remaining != null
                        ? `${Math.floor(remaining / 60)}:${String(remaining % 60).padStart(2, "0")}`
                        : "—";
                    // Checking a multi-GB file takes minutes, and on an entry with dozens of
                    // videos a file can sit in the queue for longer still. Saying so beats a bare
                    // spinner that looks like the page has hung.
                    const isQueued = progress.phase === "queued";
                    const isHashing = progress.phase === "hashing";
                    const onWire = !isQueued && !isHashing;
                    const label = isQueued ? queuePositionLabel(progress.position) : {
                        hashing: "Checking your video…",
                        resuming: "Resuming where it stopped…",
                        finalizing: "Finishing up…",
                    }[progress.phase] || "Uploading…";
                    return (
                        <div className="mt-3">
                            <div className="progress" style={{height: "20px"}}>
                                <div className={"progress-bar progress-bar-striped progress-bar-animated" + (onWire ? "" : " bg-secondary")}
                                     role="progressbar"
                                     style={{width: pct + "%"}}
                                     aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
                                    {pct}%
                                </div>
                            </div>
                            <small className="text-muted">
                                {label}
                                {/* Nothing of this file has been read yet while it waits its turn,
                                    so a "0 / 4812 MB" next to the wait would only read as stuck. */}
                                {!isQueued && ` ${(progress.loaded / 1048576).toFixed(0)} / ${(progress.total / 1048576).toFixed(0)} MB`}
                                {onWire && bps > 0 && ` · ${mbps} MB/s · ~${eta} left`}
                            </small>
                            {onWire && (
                                <div>
                                    <small className="text-muted">
                                        You can close this page if you need to — reselect the same file
                                        later and the upload carries on from here.
                                    </small>
                                </div>
                            )}
                        </div>
                    );
                })()
                :
                <div className="mt-3">
                    <Spinner />
                    <small className="text-muted ms-2">Preparing upload…</small>
                </div>
            )}
            {upload.active && <CancelUpload onCancel={cancelUpload} />}

            {/* Bounded rather than left at its intrinsic size: a 1080p (let alone 4K) recording
                rendered at full size filled the whole page, and an entry page holds one of these
                per file. Same box the Review page previews play in. */}
            <video ref={videoRef} style={PREVIEW_STYLE}
                   onError={() => fileKey && playback.onError()} onCanPlay={playback.onCanPlay}/>

            {/* An iPhone's default setting records HEVC, which Chrome and Firefox cannot decode.
                The upload is fine and the file is stored correctly — only in-browser playback
                fails, so say that instead of leaving a dead player with no explanation. */}
            {playbackUnsupported && (
                <div className="mt-1">
                    <small className="text-muted">
                        Your video was uploaded successfully, but this browser cannot play this
                        recording format.{" "}
                        <a href={process.env.REACT_APP_ENDPOINT + '/files/' + fileKey + '?redirect=1&download=1'}
                           target="_blank" rel="noreferrer">Download it</a>{" "}
                        to check it, or open this page in Safari.
                    </small>
                </div>
            )}

            {/* Last, so a refusal reads as this input's and not the next one's. */}
            {errors.map((value, index) => {
                return <Error key={index} message={value}/>;
            })}
        </div>
    );
}
